import jsPDF from "jspdf";
import { formatReportDateShort } from "./energyReportData";
import { renderDailyEnergyChartImage } from "./energyReportChart";
import { renderSankeyChartImage } from "./energyReportSankey";
import { renderHeatmapChartImage } from "./energyReportHeatmap";
import reportLogo from "../assets/logo-dark.png";

const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN = 14;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const PAGE_BORDER_INSET = 6;

const PRIMARY = { r: 27, g: 160, b: 142 };
const PRIMARY_LIGHT = { r: 236, g: 249, b: 246 };
const TEXT_DARK = { r: 30, g: 41, b: 59 };
const TEXT_MUTED = { r: 71, g: 85, b: 105 };

const LOGO_WIDTH = 46;
const LOGO_HEIGHT = 11;

const COLUMNS = [
  { key: "name", label: "Name", width: 86 },
  { key: "consumed", label: "Consumed (kWh)", width: 28, align: "right" },
  { key: "generated", label: "Generated (kWh)", width: 28, align: "right" },
  { key: "net", label: "Net (kWh)", width: 24, align: "right" },
];

const COST_COLUMNS = [
  { key: "name", label: "Name", width: 58 },
  { key: "energyBasis", label: "Energy (kWh)", width: 28, align: "right" },
  { key: "unitPrice", label: "Rate (PKR/kWh)", width: 28, align: "right" },
  { key: "estimatedCost", label: "Cost (PKR)", width: 32, align: "right" },
];

function formatKwh(value) {
  return `${Number(value || 0).toFixed(1)}`;
}

function formatPkr(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "0";
  return Math.round(num).toLocaleString(undefined, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });
}

function truncateText(doc, text, maxWidth) {
  if (!text) return "";
  if (doc.getTextWidth(text) <= maxWidth) return text;

  let truncated = text;
  while (truncated.length > 3 && doc.getTextWidth(`${truncated}...`) > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  return `${truncated}...`;
}

function drawSectionTitle(doc, y, title) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(TEXT_DARK.r, TEXT_DARK.g, TEXT_DARK.b);
  doc.text(title, MARGIN, y);
  y += 3;
  doc.setDrawColor(PRIMARY.r, PRIMARY.g, PRIMARY.b);
  doc.setLineWidth(0.8);
  doc.line(MARGIN, y, MARGIN + 42, y);
  return y + 6;
}

function drawTableHeader(doc, y, columns = COLUMNS) {
  doc.setFillColor(PRIMARY.r, PRIMARY.g, PRIMARY.b);
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.rect(MARGIN, y, CONTENT_WIDTH, 8, "F");

  let x = MARGIN + 2;
  columns.forEach((column) => {
    const textX = column.align === "right" ? x + column.width - 2 : x;
    doc.text(column.label, textX, y + 5.5, {
      align: column.align === "right" ? "right" : "left",
    });
    x += column.width;
  });

  doc.setTextColor(30, 41, 59);
  return y + 8;
}

function drawTableRow(doc, row, y) {
  const rowHeight = 7;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  let x = MARGIN + 2;

  const values = {
    name: `${"  ".repeat(row.indent || 0)}${row.name}`,
    consumed: formatKwh(row.consumed),
    generated: formatKwh(row.generated),
    net: formatKwh(row.net),
  };

  COLUMNS.forEach((column) => {
    const text = truncateText(doc, values[column.key], column.width - 4);
    const textX = column.align === "right" ? x + column.width - 2 : x;
    doc.text(text, textX, y + 4.8, {
      align: column.align === "right" ? "right" : "left",
    });
    x += column.width;
  });

  doc.setDrawColor(226, 232, 240);
  doc.line(MARGIN, y + rowHeight, MARGIN + CONTENT_WIDTH, y + rowHeight);

  return y + rowHeight;
}

function drawCostTableRow(doc, row, y) {
  const rowHeight = 7;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  let x = MARGIN + 2;

  const values = {
    name: `${"  ".repeat(row.indent || 0)}${row.name}`,
    energyBasis: formatKwh(row.energyBasis),
    unitPrice: formatPkr(row.unitPrice),
    estimatedCost: formatPkr(row.estimatedCost),
  };

  COST_COLUMNS.forEach((column) => {
    const text = truncateText(doc, values[column.key], column.width - 4);
    const textX = column.align === "right" ? x + column.width - 2 : x;
    doc.text(text, textX, y + 4.8, {
      align: column.align === "right" ? "right" : "left",
    });
    x += column.width;
  });

  doc.setDrawColor(226, 232, 240);
  doc.line(MARGIN, y + rowHeight, MARGIN + CONTENT_WIDTH, y + rowHeight);

  return y + rowHeight;
}

function loadReportLogo() {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load report logo."));
    img.src = reportLogo;
  });
}

function drawPageBorder(doc) {
  const x = PAGE_BORDER_INSET;
  const y = PAGE_BORDER_INSET;
  const w = PAGE_WIDTH - PAGE_BORDER_INSET * 2;
  const h = PAGE_HEIGHT - PAGE_BORDER_INSET * 2;

  doc.setDrawColor(PRIMARY.r, PRIMARY.g, PRIMARY.b);
  doc.setLineWidth(0.5);
  doc.rect(x, y, w, h, "S");

  doc.setFillColor(PRIMARY.r, PRIMARY.g, PRIMARY.b);
  doc.rect(x, y, w, 2.5, "F");
}

function drawMetaRow(doc, boxX, y, label, value, options = {}) {
  const { valueColor = TEXT_DARK, valueBold = false } = options;
  const labelX = boxX + 5;
  const valueX = boxX + 34;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(PRIMARY.r, PRIMARY.g, PRIMARY.b);
  doc.text(label, labelX, y);

  doc.setFont("helvetica", valueBold ? "bold" : "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(valueColor.r, valueColor.g, valueColor.b);
  doc.text(truncateText(doc, value, CONTENT_WIDTH - 42), valueX, y);

  return y + 6.5;
}

function drawReportHeader(doc, metadata, logoImage) {
  drawPageBorder(doc);

  let y = MARGIN + 2;

  if (logoImage) {
    doc.addImage(
      logoImage,
      "PNG",
      PAGE_WIDTH - MARGIN - LOGO_WIDTH,
      y - 1,
      LOGO_WIDTH,
      LOGO_HEIGHT,
    );
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(17);
  doc.setTextColor(PRIMARY.r, PRIMARY.g, PRIMARY.b);
  doc.text("Energy Consumption Report", MARGIN, y + 7);
  y += 16;

  const boxX = MARGIN;
  const boxY = y;
  const boxW = CONTENT_WIDTH;
  const boxH = 30;
  const rowStartY = boxY + 8;

  doc.setFillColor(PRIMARY_LIGHT.r, PRIMARY_LIGHT.g, PRIMARY_LIGHT.b);
  doc.setDrawColor(PRIMARY.r, PRIMARY.g, PRIMARY.b);
  doc.setLineWidth(0.6);
  doc.roundedRect(boxX, boxY, boxW, boxH, 3, 3, "FD");

  let rowY = rowStartY;
  rowY = drawMetaRow(doc, boxX, rowY, "Client", metadata.username || "N/A");
  rowY = drawMetaRow(doc, boxX, rowY, "Scope", metadata.scopeLabel || "Whole Facility", {
    valueColor: PRIMARY,
    valueBold: true,
  });
  rowY = drawMetaRow(
    doc,
    boxX,
    rowY,
    "Period",
    `${formatReportDateShort(metadata.start)} - ${formatReportDateShort(metadata.end)}`,
  );
  drawMetaRow(doc, boxX, rowY, "Generated", formatReportDateShort(new Date()), {
    valueColor: TEXT_MUTED,
  });

  return boxY + boxH + 10;
}

function drawCostSection(doc, y, costData) {
  const { costRows = [] } = costData || {};
  const sectionHeight = 16 + costRows.length * 7;
  y = ensureSpace(doc, y, sectionHeight);

  y = drawSectionTitle(doc, y, "3. Estimated Cost of Electricity");

  if (costRows.length) {
    y = drawTableHeader(doc, y, COST_COLUMNS);
    costRows.forEach((row) => {
      y = ensureSpace(doc, y, 10);
      y = drawCostTableRow(doc, row, y);
    });
    y += 4;
  }

  return y;
}

function ensureSpace(doc, y, neededHeight) {
  if (y + neededHeight <= PAGE_HEIGHT - MARGIN) return y;

  doc.addPage();
  drawPageBorder(doc);
  return MARGIN;
}

const CHART_IMAGE_HEIGHT = 72;
const SANKEY_IMAGE_HEIGHT = 95;
const HEATMAP_IMAGE_HEIGHT = 78;

function drawDailyEnergyChartSection(doc, y, dailySeries) {
  y = ensureSpace(doc, y, CHART_IMAGE_HEIGHT + 20);

  y = drawSectionTitle(doc, y, "2. Daily Energy Consumed and Generated");

  const chartImage = renderDailyEnergyChartImage(dailySeries);
  doc.addImage(chartImage, "PNG", MARGIN, y, CONTENT_WIDTH, CHART_IMAGE_HEIGHT);
  y += CHART_IMAGE_HEIGHT + 8;

  return y;
}

function drawSankeySection(doc, y, hierarchyData, scopeNodeName) {
  y = ensureSpace(doc, y, SANKEY_IMAGE_HEIGHT + 20);

  y = drawSectionTitle(doc, y, "4. Energy Flow (Sankey Diagram)");

  const sankeyImage = renderSankeyChartImage(hierarchyData, scopeNodeName, 4);
  doc.addImage(sankeyImage, "PNG", MARGIN, y, CONTENT_WIDTH, SANKEY_IMAGE_HEIGHT);
  y += SANKEY_IMAGE_HEIGHT + 8;

  return y;
}

function drawHeatmapSection(doc, y, heatmapData) {
  y = ensureSpace(doc, y, HEATMAP_IMAGE_HEIGHT + 20);

  y = drawSectionTitle(doc, y, "5. Energy Consumed Heatmap (Weekday x Hour)");

  const heatmapImage = renderHeatmapChartImage(heatmapData);
  doc.addImage(heatmapImage, "PNG", MARGIN, y, CONTENT_WIDTH, HEATMAP_IMAGE_HEIGHT);
  y += HEATMAP_IMAGE_HEIGHT + 8;

  return y;
}

export async function generateEnergyBreakdownPdf({
  rows,
  metadata,
  dailySeries,
  hierarchyData,
  scopeNodeName,
  heatmapData,
  costData,
}) {
  let logoImage = null;
  try {
    logoImage = await loadReportLogo();
  } catch {
    // PDF still generates if logo asset fails to load.
  }

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  let y = drawReportHeader(doc, metadata, logoImage);

  y = drawSectionTitle(
    doc,
    y,
    "1. Total Energy by Facility, Section, Branch and Machine",
  );

  y = ensureSpace(doc, y, 16);
  y = drawTableHeader(doc, y);

  rows.forEach((row) => {
    y = ensureSpace(doc, y, 10);
    y = drawTableRow(doc, row, y);
  });

  y += 6;
  y = ensureSpace(doc, y, CHART_IMAGE_HEIGHT + 24);
  if (y < MARGIN + 20) {
    y = MARGIN;
  }
  y = drawDailyEnergyChartSection(doc, y, dailySeries || []);

  y += 6;
  y = ensureSpace(doc, y, 40);
  if (y < MARGIN + 20) {
    y = MARGIN;
  }
  y = drawCostSection(doc, y, costData);

  y += 6;
  y = ensureSpace(doc, y, SANKEY_IMAGE_HEIGHT + 24);
  if (y < MARGIN + 20) {
    y = MARGIN;
  }
  y = drawSankeySection(doc, y, hierarchyData, scopeNodeName);

  y += 6;
  y = ensureSpace(doc, y, HEATMAP_IMAGE_HEIGHT + 24);
  if (y < MARGIN + 20) {
    y = MARGIN;
  }
  y = drawHeatmapSection(doc, y, heatmapData);

  const safeStart = formatReportDateShort(metadata.start).replace(/\s/g, "_");
  const safeEnd = formatReportDateShort(metadata.end).replace(/\s/g, "_");
  const scopeSlug = (metadata.scopeLabel || "facility")
    .replace(/[^\w]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");

  doc.save(`Energy_Report_${scopeSlug}_${safeStart}_to_${safeEnd}.pdf`);
}
